import type { Type } from '@angular/core';
import type { AuroraWidgetDef, AuroraWidgetLink, AuroraWidgetSpan } from '../aurora-widget-def';
import type { WidgetCategory } from '../widget-category';
import type { WidgetDataSource } from '../widget-data-source';
import type { TableColumnDef } from './table-widget.component';

// Re-export types so consumers can reach them via @aurora without pulling the component.
export type { TableColumnDef } from './table-widget.component';

// ─── TableWidgetConfig ────────────────────────────────────────────────────────

interface TableWidgetConfig {
  source: WidgetDataSource<Record<string, unknown>[]>;
  columns: TableColumnDef[];
  maxRows?: number;
}

// ─── defineTableWidget ────────────────────────────────────────────────────────

/** Default span for table widgets: medium-large tile with extra row height for data. */
const TABLE_DEFAULT_SPAN: AuroraWidgetSpan = {
  minCols: 3,
  maxCols: 6,
  minRows: 3,
  maxRows: 6,
  defaultCols: 4,
  defaultRows: 4,
};

export interface DefineTableWidgetConfig extends TableWidgetConfig {
  id: string;
  translation: string;
  iconUrl: string;
  description?: string;
  permission?: string | string[];
  category?: WidgetCategory;
  span?: AuroraWidgetSpan;
  link?: AuroraWidgetLink;
}

/**
 * Factory that converts a typed `DefineTableWidgetConfig` into an
 * `AuroraWidgetDef`. The archetype config fields are placed in `inputs`
 * for forwarding via `[ngComponentOutletInputs]`.
 */
export function defineTableWidget(config: DefineTableWidgetConfig): AuroraWidgetDef {
  const { id, translation, iconUrl, description, permission, category, span, link } = config;
  const { source, columns, maxRows } = config;

  return {
    id,
    translation,
    iconUrl,
    description,
    permission,
    category,
    link,
    span: span ?? TABLE_DEFAULT_SPAN,
    inputs: {
      source,
      columns,
      ...(maxRows !== undefined && { maxRows }),
    },
    component: (): Promise<Type<unknown>> =>
      import('./table-widget.component').then((m) => m.TableWidgetComponent as Type<unknown>),
  };
}
